import React, { useState } from "react";
import styled from "styled-components";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SavedAddressPicker from "../Address/SavedAddressPicker";
import AddressAutocomplete from "../Address/AddressAutocomplete";
import { savedAddressLatLng } from "../../utils/shippingValidation";
import {
  FieldLabel,
  GREEN_SOFT,
  INK,
  MUTED,
  Notice,
  TEXT,
  TextInput,
} from "./groupBasketStyles";

/*
 * Where the one rider delivers: the host's drop-off, picked the way every
 * checkout picks it. A saved address (SavedAddressPicker) or a Google Places
 * search (AddressAutocomplete), both resolving to coordinates, plus the
 * landmark and the rider's contact number. The value is the contract's
 * `dropoff` (an order's shippingAddress shape) with the saved address id the
 * picker highlights.
 */
const GroupDropoffPicker = ({ value, onChange, autoSelectSaved = true }) => {
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState("");
  const set = (patch) => onChange({ ...value, ...patch });

  const pickSaved = (address) => {
    const latLng = savedAddressLatLng(address);
    if (!latLng) {
      setError(
        "That saved address has no map pin. Search the address below instead.",
      );
      return;
    }
    setError("");
    set({
      address: address.address || address.label || "",
      description: address.description || value.description || "",
      latLng,
      customerNumber: value.customerNumber || address.recipientPhone || "",
      source: "places",
      savedAddressId: address._id || null,
    });
  };

  const pickSearched = ({ latLng, formatted }) => {
    setError("");
    setSearchText("");
    set({ address: formatted, latLng, source: "places", savedAddressId: null });
  };

  return (
    <Wrap>
      {value.address && value.latLng ? (
        <Chosen>
          <PlaceOutlinedIcon aria-hidden="true" />
          <span>
            <strong>Deliver to</strong>
            {value.address}
          </span>
        </Chosen>
      ) : null}

      <SavedAddressPicker
        onSelect={pickSaved}
        selectedId={value.savedAddressId || null}
        autoSelect={autoSelectSaved}
      />

      <div>
        <FieldLabel as="span">
          {value.address ? "Or search another address" : "Search an address"}
        </FieldLabel>
        <AddressAutocomplete
          value={searchText}
          onChange={setSearchText}
          onResolved={pickSearched}
          onError={() =>
            setError(
              "We could not place that address on the map. Try another search.",
            )
          }
          placeholder="Street, building or area"
        />
      </div>

      <div>
        <FieldLabel htmlFor="group-dropoff-description">
          Gate, floor or landmark (optional)
        </FieldLabel>
        <TextInput
          id="group-dropoff-description"
          value={value.description || ""}
          maxLength={120}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="Blue gate, second floor"
        />
      </div>

      <div>
        <FieldLabel htmlFor="group-dropoff-phone">
          Number to call for delivery!
        </FieldLabel>
        <TextInput
          id="group-dropoff-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={value.customerNumber || ""}
          onChange={(e) => set({ customerNumber: e.target.value })}
          placeholder="07XX XXX XXX"
        />
      </div>

      {error ? (
        <Notice $tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  /* The shared address search field, at this sheet's size. */
  [data-reach-combobox-input] {
    min-height: 48px;
    font-size: 16px !important;
    border-radius: 12px !important;
  }
`;

const Chosen = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${GREEN_SOFT};
  color: ${TEXT};
  font-size: 15px;
  line-height: 1.4;

  svg {
    color: ${INK};
    flex-shrink: 0;
  }

  strong {
    display: block;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${MUTED};
  }
`;

export default GroupDropoffPicker;
